import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import {
  FaUserCheck, FaUserTimes, FaClock, FaUserFriends, FaExclamationTriangle,
  FaSearch, FaFilter, FaDownload, FaPrint, FaArrowLeft, FaCheck, FaTimes,
  FaCalendarAlt, FaEnvelope, FaPhone, FaBuilding, FaAward, FaSyncAlt,
  FaChartPie, FaThLarge, FaList, FaEdit, FaSave, FaExternalLinkAlt, FaTimesCircle,
  FaCheckSquare, FaSquare
} from "react-icons/fa";

export interface MemberSummary {
  user_id: number;
  name: string;
  email: string;
  number: string;
  company_name: string;
  designation: string;
  profile: string;
  society_year?: string;
  total_meetings: number;
  effective_meetings: number;
  total_present: number;
  total_absent: number;
  total_late: number;
  total_substituted: number;
  total_na: number;
  total_attended: number;
  attendance_percentage: number;
  consecutive_absences: number;
  flagged_3_absences: boolean;
  attendance_points: number;
  user_points?: number;
}

export interface MemberMeetingHistory {
  id?: number | null;
  event_id: string | number;
  raw_id?: string | number;
  event_title: string;
  event_date: string;
  event_location?: string;
  event_type?: string;
  status: 'Present' | 'Absent' | 'Late' | 'Substituted' | 'NA';
  points: number;
  substitute_name?: string;
  substitute_phone?: string;
  substitute_company?: string;
  notes?: string;
  updated_at?: string;
}

interface ChapterStats {
  total_members: number;
  avg_attendance: number;
  total_present: number;
  total_absent: number;
  total_late: number;
  total_substituted: number;
  flagged_members_count: number;
}

interface ChapterEventItem {
  id: string | number;
  raw_id: number;
  title: string;
  date: string;
  event_type: string;
  location: string;
}

interface UserRosterItem {
  user_id: number;
  name: string;
  email: string;
  number: string;
  company_name: string;
  designation: string;
  profile: string;
  society_year?: string;
  user_points?: number;
}

interface MemberAttendanceProps {
  initialUserId?: number | null;
  onNavigateToBatch?: (eventId?: string | number) => void;
}

export const EVENT_TYPE_CONFIG: Record<string, { label: string; color: string; bg: string; border: string; activeBorder: string; badgeBg: string; icon: string }> = {
  'General Meeting': {
    label: 'General Meeting',
    color: 'text-purple-700 dark:text-purple-300',
    bg: 'bg-purple-50 dark:bg-purple-950/40',
    border: 'border-purple-200 dark:border-purple-800/60',
    activeBorder: 'border-purple-500 ring-2 ring-purple-400/30',
    badgeBg: 'bg-purple-100 dark:bg-purple-900/60 text-purple-800 dark:text-purple-200',
    icon: '📋'
  },
  'Meeting': {
    label: 'Meeting',
    color: 'text-blue-700 dark:text-blue-300',
    bg: 'bg-blue-50 dark:bg-blue-950/40',
    border: 'border-blue-200 dark:border-blue-800/60',
    activeBorder: 'border-blue-500 ring-2 ring-blue-400/30',
    badgeBg: 'bg-blue-100 dark:bg-blue-900/60 text-blue-800 dark:text-blue-200',
    icon: '🤝'
  },
  'Field Visit': {
    label: 'Field Visit',
    color: 'text-emerald-700 dark:text-emerald-300',
    bg: 'bg-emerald-50 dark:bg-emerald-950/40',
    border: 'border-emerald-200 dark:border-emerald-800/60',
    activeBorder: 'border-emerald-500 ring-2 ring-emerald-400/30',
    badgeBg: 'bg-emerald-100 dark:bg-emerald-900/60 text-emerald-800 dark:text-emerald-200',
    icon: '🏭'
  },
  'Installation': {
    label: 'Installation Ceremony',
    color: 'text-amber-700 dark:text-amber-300',
    bg: 'bg-amber-50 dark:bg-amber-950/40',
    border: 'border-amber-200 dark:border-amber-800/60',
    activeBorder: 'border-amber-500 ring-2 ring-amber-400/30',
    badgeBg: 'bg-amber-100 dark:bg-amber-900/60 text-amber-800 dark:text-amber-200',
    icon: '👑'
  },
  'Webinar': {
    label: 'Webinar',
    color: 'text-indigo-700 dark:text-indigo-300',
    bg: 'bg-indigo-50 dark:bg-indigo-950/40',
    border: 'border-indigo-200 dark:border-indigo-800/60',
    activeBorder: 'border-indigo-500 ring-2 ring-indigo-400/30',
    badgeBg: 'bg-indigo-100 dark:bg-indigo-900/60 text-indigo-800 dark:text-indigo-200',
    icon: '💻'
  },
  'Social': {
    label: 'Social Event',
    color: 'text-pink-700 dark:text-pink-300',
    bg: 'bg-pink-50 dark:bg-pink-950/40',
    border: 'border-pink-200 dark:border-pink-800/60',
    activeBorder: 'border-pink-500 ring-2 ring-pink-400/30',
    badgeBg: 'bg-pink-100 dark:bg-pink-900/60 text-pink-800 dark:text-pink-200',
    icon: '🎉'
  },
  'Other': {
    label: 'Other Event',
    color: 'text-slate-700 dark:text-slate-300',
    bg: 'bg-slate-50 dark:bg-white/5',
    border: 'border-slate-200 dark:border-white/10',
    activeBorder: 'border-slate-500 ring-2 ring-slate-400/30',
    badgeBg: 'bg-slate-100 dark:bg-white/10 text-slate-700 dark:text-slate-300',
    icon: '📌'
  },
};

const cleanText = (str: string): string => {
  if (!str) return '';
  return str
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim();
};

const MemberAttendance: React.FC<MemberAttendanceProps> = ({ initialUserId, onNavigateToBatch }) => {
  // Current Society Year computation (Rotary Society Year: July 1 to June 30)
  const now = new Date();
  let startYear = now.getFullYear();
  let endYear = startYear + 1;
  if (now.getMonth() < 6) {
    startYear = now.getFullYear() - 1;
    endYear = now.getFullYear();
  }
  const currentSocietyYear = `${startYear}-${endYear}`;

  const [societyYear, setSocietyYear] = useState<string>(currentSocietyYear);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<'ALL' | 'EXCELLENT' | 'AVERAGE' | 'CRITICAL' | 'FLAGGED'>('ALL');
  const [sortBy, setSortBy] = useState<'PERCENT_DESC' | 'PERCENT_ASC' | 'PRESENT_DESC' | 'ABSENT_DESC' | 'NAME_ASC' | 'STREAK_DESC'>('PERCENT_DESC');
  const [viewMode, setViewMode] = useState<'TABLE' | 'GRID'>('TABLE');

  // Core Raw Data for Dynamic Client-Side Calculation
  const [rawEvents, setRawEvents] = useState<ChapterEventItem[]>([]);
  const [usersRoster, setUsersRoster] = useState<UserRosterItem[]>([]);
  const [rawAttendanceMap, setRawAttendanceMap] = useState<Map<string, any>>(new Map());

  // Event Types Selection Checkbox State
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([]);

  // Computed Members & Chapter Stats
  const [members, setMembers] = useState<MemberSummary[]>([]);
  const [chapterStats, setChapterStats] = useState<ChapterStats>({
    total_members: 0,
    avg_attendance: 0,
    total_present: 0,
    total_absent: 0,
    total_late: 0,
    total_substituted: 0,
    flagged_members_count: 0
  });

  // Selected Member for Individual Attendance Drilldown Sheet Modal
  const [selectedMember, setSelectedMember] = useState<MemberSummary | null>(null);
  const [memberHistory, setMemberHistory] = useState<MemberMeetingHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);
  const [filterModalBySelectedTypes, setFilterModalBySelectedTypes] = useState<boolean>(true);

  // Single Record Inline Edit Modal state
  const [editingRecord, setEditingRecord] = useState<MemberMeetingHistory | null>(null);
  const [editStatus, setEditStatus] = useState<'Present' | 'Absent' | 'Late' | 'Substituted' | 'NA'>('Present');
  const [editSubName, setEditSubName] = useState('');
  const [editSubPhone, setEditSubPhone] = useState('');
  const [editSubCompany, setEditSubCompany] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [savingRecord, setSavingRecord] = useState(false);
  const [recordSaveMsg, setRecordSaveMsg] = useState('');

  // 1. Discover all unique event types from the current loaded events
  const availableEventTypes = useMemo(() => {
    const typesSet = new Set<string>();
    rawEvents.forEach(e => {
      if (e.event_type) typesSet.add(e.event_type);
    });
    // Ensure default common types are available if empty
    if (typesSet.size === 0) {
      typesSet.add('General Meeting');
      typesSet.add('Field Visit');
    }
    return Array.from(typesSet);
  }, [rawEvents]);

  // Event count per type in the current society year
  const eventTypeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    rawEvents.forEach(e => {
      const t = e.event_type || 'General Meeting';
      counts[t] = (counts[t] || 0) + 1;
    });
    return counts;
  }, [rawEvents]);

  // 2. Main Data Fetching: Events, Roster, and Sheets
  const fetchSummary = async () => {
    setLoading(true);
    try {
      // Fetch Attendance Events, Calendar Events for precise type mapping, Active Members, and Backend Summary in parallel
      const [eventsRes, allEventsRes, usersRes, summaryRes] = await Promise.all([
        axios.get(`https://rmbbackend.rmbjaipur.com/auth/attendance/events?society_year=${societyYear}`).catch(() => ({ data: { events: [] } })),
        axios.get('https://rmbbackend.rmbjaipur.com/auth/events').catch(() => ({ data: [] })),
        axios.get('https://rmbbackend.rmbjaipur.com/auth/allusersinfo').catch(() => ({ data: [] })),
        axios.get(`https://rmbbackend.rmbjaipur.com/auth/attendance/members-summary?society_year=${societyYear}`).catch(() => ({ data: null }))
      ]);

      // Calendar Events mapping lookup by ID
      const calList = allEventsRes.data?.events || allEventsRes.data || [];
      const calMap = new Map<number, any>();
      (calList || []).forEach((c: any) => {
        if (c.id) calMap.set(Number(c.id), c);
      });

      // Normalize events with exact event_type
      const rawEvs = eventsRes.data?.events || [];
      const normalizedEvents: ChapterEventItem[] = rawEvs.map((e: any) => {
        const rawId = Number(e.raw_id || String(e.id).replace(/^cal_/, ''));
        const cal = calMap.get(rawId);
        let evType = e.event_type;
        if (!evType || evType === 'CALENDAR_EVENT' || evType === 'CHAPTER_MEETING') {
          if (cal?.event_type) {
            evType = cal.event_type;
          } else {
            const t = (e.title || '').toLowerCase();
            if (t.includes('field visit')) evType = 'Field Visit';
            else if (t.includes('webinar')) evType = 'Webinar';
            else if (t.includes('installation')) evType = 'Installation';
            else if (t.includes('social')) evType = 'Social';
            else evType = 'General Meeting';
          }
        }
        return {
          id: e.id,
          raw_id: rawId,
          title: cleanText(e.title || `Event #${e.id}`),
          date: e.date || '',
          event_type: evType,
          location: cal?.location || ''
        };
      });

      setRawEvents(normalizedEvents);

      // Extract discovered types and initialize selected checkboxes
      const discovered = Array.from(new Set(normalizedEvents.map(e => e.event_type)));
      if (discovered.length > 0) {
        setSelectedEventTypes(discovered);
      } else {
        setSelectedEventTypes(['General Meeting', 'Field Visit']);
      }

      // Active member roster
      const rawUsers = usersRes.data?.data || usersRes.data || [];
      const activeMembers: UserRosterItem[] = (rawUsers || [])
        .filter((u: any) => u.delete !== 1 && u.role !== 'super-admin' && (u.status || 'active').toLowerCase() === 'active')
        .map((u: any) => ({
          user_id: u.id,
          name: u.name || 'Member',
          email: u.email || '',
          number: String(u.number || ''),
          company_name: u.company_name || '',
          designation: u.designation || '',
          profile: u.profile || '',
          society_year: u.society_year || societyYear,
          user_points: u.point || 0
        }));

      setUsersRoster(activeMembers);

      // Fetch all event attendance sheets in parallel for live, zero-latency recalculation
      if (normalizedEvents.length > 0) {
        const sheetResults = await Promise.all(
          normalizedEvents.map(ev =>
            axios.get(`https://rmbbackend.rmbjaipur.com/auth/attendance/event-sheet/${ev.id}`)
              .then(res => ({ eventId: ev.id, rows: res.data?.eventSheet || [] }))
              .catch(() => ({ eventId: ev.id, rows: [] }))
          )
        );

        const newAttMap = new Map<string, any>();
        sheetResults.forEach(({ eventId, rows }) => {
          rows.forEach((row: any) => {
            newAttMap.set(`${eventId}_${row.user_id}`, row);
            const cleanId = String(eventId).replace(/^cal_/, '');
            newAttMap.set(`${cleanId}_${row.user_id}`, row);
          });
        });

        setRawAttendanceMap(newAttMap);
      } else if (summaryRes.data?.success && summaryRes.data?.members) {
        // Fallback to backend summary if no event sheets
        setMembers(summaryRes.data.members || []);
        if (summaryRes.data.chapter_stats) {
          setChapterStats(summaryRes.data.chapter_stats);
        }
      }
    } catch (err) {
      console.error('Error fetching attendance data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
  }, [societyYear]);

  // 3. Dynamic Attendance Calculation Engine
  // Calculates attendance for all members and chapter stats strictly based on selectedEventTypes
  const calculateAttendanceForSelectedTypes = useCallback((
    events: ChapterEventItem[],
    roster: UserRosterItem[],
    attMap: Map<string, any>,
    types: string[]
  ) => {
    // If no roster or events, clear
    if (roster.length === 0) return;

    // Filter events matching selected event types
    const matchingEvents = events
      .filter(e => types.includes(e.event_type))
      .sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime()); // Newest first for streak

    const totalMatching = matchingEvents.length;

    let chapterPres = 0;
    let chapterAbs = 0;
    let chapterLate = 0;
    let chapterSub = 0;
    let flaggedMembers = 0;

    const computedMembers: MemberSummary[] = roster.map(u => {
      let pres = 0;
      let abs = 0;
      let late = 0;
      let sub = 0;
      let na = 0;
      let streak = 0;
      let streakActive = true;

      matchingEvents.forEach(ev => {
        const key1 = `${ev.id}_${u.user_id}`;
        const key2 = `${String(ev.id).replace(/^cal_/, '')}_${u.user_id}`;
        const att = attMap.get(key1) || attMap.get(key2);
        const status = att ? att.status : 'Absent';
        const norm = (status || '').trim().toLowerCase();

        if (norm === 'present') {
          pres++;
          if (streakActive) streakActive = false;
        } else if (norm === 'late') {
          late++;
          if (streakActive) streakActive = false;
        } else if (norm === 'substituted' || norm === 'substitute') {
          sub++;
          if (streakActive) streakActive = false;
        } else if (norm === 'na') {
          na++;
          // NA does not add to absences or break streak
        } else {
          // Absent or unmarked
          abs++;
          if (streakActive) streak++;
        }
      });

      const effective = Math.max(0, totalMatching - na);
      const attended = pres + late + sub;
      const pct = effective > 0 ? Math.round((attended / effective) * 100) : 0;
      const isFlagged = streak >= 3;
      if (isFlagged) flaggedMembers++;

      chapterPres += pres;
      chapterAbs += abs;
      chapterLate += late;
      chapterSub += sub;

      const points = (pres * 10) + (sub * 10) + (late * 5);

      return {
        user_id: u.user_id,
        name: u.name,
        email: u.email,
        number: u.number,
        company_name: u.company_name,
        designation: u.designation,
        profile: u.profile,
        society_year: u.society_year || societyYear,
        total_meetings: totalMatching,
        effective_meetings: effective,
        total_present: pres,
        total_absent: abs,
        total_late: late,
        total_substituted: sub,
        total_na: na,
        total_attended: attended,
        attendance_percentage: pct,
        consecutive_absences: streak,
        flagged_3_absences: isFlagged,
        attendance_points: points,
        user_points: u.user_points || 0
      };
    });

    const totalActiveMembers = computedMembers.length;
    const avgRate = totalActiveMembers > 0
      ? Math.round(computedMembers.reduce((sum, m) => sum + m.attendance_percentage, 0) / totalActiveMembers)
      : 0;

    setMembers(computedMembers);
    setChapterStats({
      total_members: totalActiveMembers,
      avg_attendance: avgRate,
      total_present: chapterPres,
      total_absent: chapterAbs,
      total_late: chapterLate,
      total_substituted: chapterSub,
      flagged_members_count: flaggedMembers
    });

    // If a member sheet modal is currently open, sync its summary stats
    setSelectedMember(prev => {
      if (!prev) return null;
      const found = computedMembers.find(m => m.user_id === prev.user_id);
      return found ? { ...prev, ...found } : prev;
    });
  }, [societyYear]);

  // Recalculate whenever selectedEventTypes, rawAttendanceMap, usersRoster, or rawEvents change
  useEffect(() => {
    if (usersRoster.length > 0 && rawEvents.length > 0) {
      calculateAttendanceForSelectedTypes(rawEvents, usersRoster, rawAttendanceMap, selectedEventTypes);
    }
  }, [selectedEventTypes, rawEvents, usersRoster, rawAttendanceMap, calculateAttendanceForSelectedTypes]);

  // Handle Event Type Checkbox Toggle
  const handleToggleEventType = (type: string) => {
    setSelectedEventTypes(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  const handleSelectAllEventTypes = () => {
    setSelectedEventTypes([...availableEventTypes]);
  };

  const handleClearAllEventTypes = () => {
    setSelectedEventTypes([]);
  };

  // Handle auto-opening member sheet if initialUserId provided or in localStorage
  useEffect(() => {
    const preselectedId = initialUserId || localStorage.getItem('selected_member_attendance_id');
    if (preselectedId && members.length > 0) {
      const found = members.find(m => String(m.user_id) === String(preselectedId));
      if (found) {
        handleOpenMemberSheet(found);
      }
      localStorage.removeItem('selected_member_attendance_id');
    }
  }, [members, initialUserId]);

  // 4. Fetch Single Member Attendance Sheet & Meeting Breakdown
  const handleOpenMemberSheet = async (member: MemberSummary) => {
    setSelectedMember(member);
    setHistoryLoading(true);
    try {
      // Build history directly from loaded normalized events and attendance map
      if (rawEvents.length > 0) {
        const historyList: MemberMeetingHistory[] = rawEvents
          .sort((a, b) => new Date(b.date || 0).getTime() - new Date(a.date || 0).getTime())
          .map(ev => {
            const key1 = `${ev.id}_${member.user_id}`;
            const key2 = `${String(ev.id).replace(/^cal_/, '')}_${member.user_id}`;
            const att = rawAttendanceMap.get(key1) || rawAttendanceMap.get(key2);
            const status = att ? att.status : 'Absent';
            const norm = (status || '').trim().toLowerCase();
            const pts = norm === 'present' ? 10 : norm === 'late' ? 5 : norm === 'substituted' || norm === 'substitute' ? 10 : 0;

            return {
              id: att?.id || null,
              event_id: ev.id,
              raw_id: ev.raw_id,
              event_title: ev.title,
              event_date: ev.date,
              event_location: ev.location,
              event_type: ev.event_type || 'General Meeting',
              status: status,
              points: pts,
              substitute_name: att?.substitute_name || '',
              substitute_phone: att?.substitute_phone || '',
              substitute_company: att?.substitute_company || '',
              notes: att?.notes || '',
              updated_at: att?.updated_at || undefined
            };
          });

        setMemberHistory(historyList);
      } else {
        // Fallback to backend member-sheet endpoint
        const res = await axios.get(`https://rmbbackend.rmbjaipur.com/auth/attendance/member-sheet/${member.user_id}?society_year=${societyYear}`);
        if (res.data?.success && res.data.history) {
          setMemberHistory(res.data.history);
        }
      }
    } catch (e) {
      console.error('Failed to load member attendance history:', e);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Filtered Member History inside Drilldown Modal
  const modalDisplayHistory = useMemo(() => {
    if (!filterModalBySelectedTypes) return memberHistory;
    return memberHistory.filter(h => selectedEventTypes.includes(h.event_type || 'General Meeting'));
  }, [memberHistory, filterModalBySelectedTypes, selectedEventTypes]);

  // 5. Quick Edit Single Attendance Record
  const handleStartEdit = (record: MemberMeetingHistory) => {
    setEditingRecord(record);
    setEditStatus(record.status);
    setEditSubName(record.substitute_name || '');
    setEditSubPhone(record.substitute_phone || '');
    setEditSubCompany(record.substitute_company || '');
    setEditNotes(record.notes || '');
    setRecordSaveMsg('');
  };

  const handleSaveSingleRecord = async () => {
    if (!editingRecord || !selectedMember) return;
    setSavingRecord(true);
    setRecordSaveMsg('');
    try {
      await axios.post('https://rmbbackend.rmbjaipur.com/auth/attendance/update-single-record', {
        event_id: editingRecord.event_id,
        user_id: selectedMember.user_id,
        status: editStatus,
        substitute_name: editStatus === 'Substituted' ? editSubName : null,
        substitute_phone: editStatus === 'Substituted' ? editSubPhone : null,
        substitute_company: editStatus === 'Substituted' ? editSubCompany : null,
        notes: editNotes
      });

      // Update in rawAttendanceMap locally
      const recordKey1 = `${editingRecord.event_id}_${selectedMember.user_id}`;
      const recordKey2 = `${String(editingRecord.event_id).replace(/^cal_/, '')}_${selectedMember.user_id}`;
      const pts = editStatus === 'Present' ? 10 : editStatus === 'Late' ? 5 : editStatus === 'Substituted' ? 10 : 0;

      const updatedRow = {
        status: editStatus,
        points: pts,
        substitute_name: editStatus === 'Substituted' ? editSubName : '',
        substitute_phone: editStatus === 'Substituted' ? editSubPhone : '',
        substitute_company: editStatus === 'Substituted' ? editSubCompany : '',
        notes: editNotes,
        updated_at: new Date().toISOString()
      };

      setRawAttendanceMap(prev => {
        const next = new Map(prev);
        next.set(recordKey1, updatedRow);
        next.set(recordKey2, updatedRow);
        return next;
      });

      // Update state locally in memberHistory
      setMemberHistory(prev => prev.map(rec => {
        if (rec.event_id === editingRecord.event_id) {
          return {
            ...rec,
            status: editStatus,
            points: pts,
            substitute_name: editStatus === 'Substituted' ? editSubName : '',
            substitute_phone: editStatus === 'Substituted' ? editSubPhone : '',
            substitute_company: editStatus === 'Substituted' ? editSubCompany : '',
            notes: editNotes
          };
        }
        return rec;
      }));

      setRecordSaveMsg('✅ Attendance status updated successfully!');
      setTimeout(() => {
        setEditingRecord(null);
        setRecordSaveMsg('');
      }, 1000);
    } catch (err: any) {
      console.error('Error updating attendance record:', err);
      setRecordSaveMsg('❌ Failed to update record.');
    } finally {
      setSavingRecord(false);
    }
  };

  // 6. Filtering and Sorting
  const filteredMembers = useMemo(() => {
    return members
      .filter(m => {
        // Search
        if (searchQuery.trim()) {
          const q = searchQuery.toLowerCase();
          const match =
            (m.name || '').toLowerCase().includes(q) ||
            (m.company_name || '').toLowerCase().includes(q) ||
            (m.designation || '').toLowerCase().includes(q) ||
            (m.email || '').toLowerCase().includes(q) ||
            (m.number || '').toLowerCase().includes(q);
          if (!match) return false;
        }

        // Status Filter
        if (statusFilter === 'EXCELLENT') return m.attendance_percentage >= 80;
        if (statusFilter === 'AVERAGE') return m.attendance_percentage >= 50 && m.attendance_percentage < 80;
        if (statusFilter === 'CRITICAL') return m.attendance_percentage < 50;
        if (statusFilter === 'FLAGGED') return m.flagged_3_absences || m.consecutive_absences >= 3;

        return true;
      })
      .sort((a, b) => {
        if (sortBy === 'PERCENT_DESC') return b.attendance_percentage - a.attendance_percentage;
        if (sortBy === 'PERCENT_ASC') return a.attendance_percentage - b.attendance_percentage;
        if (sortBy === 'PRESENT_DESC') return b.total_present - a.total_present;
        if (sortBy === 'ABSENT_DESC') return b.total_absent - a.total_absent;
        if (sortBy === 'STREAK_DESC') return b.consecutive_absences - a.consecutive_absences;
        if (sortBy === 'NAME_ASC') return (a.name || '').localeCompare(b.name || '');
        return 0;
      });
  }, [members, searchQuery, statusFilter, sortBy]);

  // Matching events count for selected types
  const filteredEventsCount = useMemo(() => {
    return rawEvents.filter(e => selectedEventTypes.includes(e.event_type)).length;
  }, [rawEvents, selectedEventTypes]);

  // 7. CSV Export Functions
  const exportAllSummaryToCSV = () => {
    const headers = [
      "Member ID", "Full Name", "Company Name", "Designation", "Email", "Phone",
      "Total Meetings", "Present", "Absent", "Late", "Substituted", "NA",
      "Attendance %", "Consecutive Absences", "Flagged (3+ Absences)", "Attendance Points"
    ];

    const rows = filteredMembers.map(m => [
      m.user_id,
      `"${(m.name || '').replace(/"/g, '""')}"`,
      `"${(m.company_name || '').replace(/"/g, '""')}"`,
      `"${(m.designation || '').replace(/"/g, '""')}"`,
      m.email || '',
      m.number || '',
      m.total_meetings,
      m.total_present,
      m.total_absent,
      m.total_late,
      m.total_substituted,
      m.total_na,
      `${m.attendance_percentage}%`,
      m.consecutive_absences,
      m.flagged_3_absences ? "YES" : "NO",
      m.attendance_points
    ]);

    const csvContent = "data:text/csv;charset=utf-8," + [
      `"RMB Jaipur Member Attendance Summary - Society Year: ${societyYear}"`,
      `"Selected Event Types: ${selectedEventTypes.join(', ') || 'None'} (${filteredEventsCount} meetings calculated)"`,
      `"Exported on ${new Date().toLocaleDateString('en-IN')}"`,
      "",
      headers.join(","),
      ...rows.map(e => e.join(","))
    ].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    const typesSlug = selectedEventTypes.length === availableEventTypes.length ? "All_Types" : selectedEventTypes.join('_');
    link.setAttribute("download", `RMB_Jaipur_Attendance_${typesSlug}_${societyYear}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportSingleMemberToCSV = () => {
    if (!selectedMember) return;
    const headers = ["Meeting Date", "Meeting / Event Title", "Type", "Status", "Points", "Substitute Name", "Substitute Phone", "Substitute Company", "Notes"];
    const rows = modalDisplayHistory.map(h => [
      h.event_date ? new Date(h.event_date).toLocaleDateString('en-IN') : '',
      `"${(h.event_title || '').replace(/"/g, '""')}"`,
      h.event_type || 'General Meeting',
      h.status,
      h.points,
      `"${(h.substitute_name || '').replace(/"/g, '""')}"`,
      h.substitute_phone || '',
      `"${(h.substitute_company || '').replace(/"/g, '""')}"`,
      `"${(h.notes || '').replace(/"/g, '""')}"`
    ]);

    const csvContent = "data:text/csv;charset=utf-8," + [
      `"Member Attendance Ledger: ${selectedMember.name} (${selectedMember.company_name || ''}) - Society Year: ${societyYear}"`,
      `"Selected Event Types: ${selectedEventTypes.join(', ') || 'All'} (${modalDisplayHistory.length} meetings)"`,
      `"Total Meetings: ${selectedMember.total_meetings}, Present: ${selectedMember.total_present}, Absent: ${selectedMember.total_absent}, Late: ${selectedMember.total_late}, Substituted: ${selectedMember.total_substituted}, Attendance Rate: ${selectedMember.attendance_percentage}%"`,
      "",
      headers.join(","),
      ...rows.map(e => e.join(","))
    ].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Attendance_${selectedMember.name.replace(/\s+/g, '_')}_${societyYear}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto space-y-6">
      {/* ================= HEADER SECTION ================= */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-gradient-to-r from-[#0A2451] via-[#103366] to-[#0A2451] p-5 sm:p-6 rounded-2xl shadow-xl border border-white/10 text-white">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="px-2.5 py-0.5 rounded-full text-[11px] font-black uppercase tracking-wider bg-[#FDBC12] text-[#0A2451]">
              Chapter Operations
            </span>
            <span className="text-xs text-amber-200 font-semibold">Society Year {societyYear}</span>
          </div>
          <h1 className="text-xl sm:text-2xl font-black flex items-center gap-2 text-white">
            <FaUserCheck className="text-[#FDBC12]" />
            Member-Wise Attendance Ledger
          </h1>
          <p className="text-xs sm:text-sm text-slate-300 mt-1 max-w-2xl">
            Filter by event types via checkboxes to recalculate attendance percentage, participation points, and absence streaks on the fly.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          {/* Society Year Dropdown */}
          <div className="flex items-center gap-1.5 bg-white/10 backdrop-blur-md px-3 py-1.5 rounded-xl border border-white/15 text-xs font-bold">
            <FaCalendarAlt className="text-[#FDBC12]" />
            <span>Year:</span>
            <select
              value={societyYear}
              onChange={(e) => setSocietyYear(e.target.value)}
              className="bg-transparent text-white font-bold outline-none cursor-pointer"
            >
              <option value={`${startYear}-${endYear}`} className="bg-[#0A2451] text-white">Current ({startYear}-{endYear})</option>
              <option value={`${startYear - 1}-${startYear}`} className="bg-[#0A2451] text-white">{startYear - 1}-{startYear}</option>
              <option value={`${startYear - 2}-${startYear - 1}`} className="bg-[#0A2451] text-white">{startYear - 2}-{startYear - 1}</option>
              <option value="all" className="bg-[#0A2451] text-white">All Society Years</option>
            </select>
          </div>

          {/* Refresh Button */}
          <button
            onClick={fetchSummary}
            disabled={loading}
            className="p-2 rounded-xl bg-white/10 hover:bg-white/20 active:scale-95 transition cursor-pointer text-white"
            title="Refresh Data"
          >
            <FaSyncAlt className={loading ? "animate-spin" : ""} />
          </button>

          {/* Export All CSV */}
          <button
            onClick={exportAllSummaryToCSV}
            className="px-3 py-1.5 rounded-xl bg-[#FDBC12] hover:bg-amber-400 active:scale-95 text-[#0A2451] font-black text-xs flex items-center gap-1.5 transition cursor-pointer shadow-md"
            title="Download CSV of all members summary based on selected event types"
          >
            <FaDownload />
            <span>Export CSV</span>
          </button>

          {/* Switch to Batch Attendance */}
          {onNavigateToBatch && (
            <button
              onClick={() => onNavigateToBatch()}
              className="px-3 py-1.5 rounded-xl bg-white/15 hover:bg-white/25 active:scale-95 text-white font-bold text-xs flex items-center gap-1.5 transition cursor-pointer border border-white/20"
              title="Open Event Batch Attendance Sheet"
            >
              <FaCalendarAlt className="text-amber-300" />
              <span>Batch Sheet</span>
            </button>
          )}
        </div>
      </div>

      {/* ================= DYNAMIC EVENT TYPE CHECKBOX SELECTION & CALCULATION CONTROLS ================= */}
      <div className="bg-white dark:bg-[#131E32] p-4 sm:p-5 rounded-2xl shadow-sm border border-slate-200 dark:border-white/10 space-y-3.5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 dark:border-white/5 pb-3">
          <div className="flex items-center gap-2.5">
            <span className="p-2.5 rounded-xl bg-[#0A2451] text-[#FDBC12] text-sm shadow-xs flex-shrink-0">
              <FaFilter />
            </span>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-black text-slate-800 dark:text-white">
                  Event Types for Attendance Calculation
                </h3>
                <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-[#0A2451]/10 dark:bg-white/10 text-[#0A2451] dark:text-[#FDBC12]">
                  {selectedEventTypes.length} of {availableEventTypes.length} Active
                </span>
              </div>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                Toggle checkboxes below to recalculate member attendance %, absences, points, and streaks based solely on selected events.
              </p>
            </div>
          </div>

          {/* Quick Select / Deselect Actions */}
          <div className="flex items-center gap-2 self-start sm:self-auto flex-shrink-0">
            <button
              type="button"
              onClick={handleSelectAllEventTypes}
              className="px-3 py-1.5 text-xs font-bold rounded-xl bg-slate-100 hover:bg-[#FDBC12] hover:text-[#0A2451] dark:bg-white/10 dark:hover:bg-[#FDBC12] dark:hover:text-[#0A2451] text-slate-700 dark:text-slate-200 transition cursor-pointer"
            >
              Select All
            </button>
            <button
              type="button"
              onClick={handleClearAllEventTypes}
              className="px-3 py-1.5 text-xs font-bold rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-white/10 dark:hover:bg-white/20 text-slate-600 dark:text-slate-300 transition cursor-pointer"
            >
              Clear All
            </button>
          </div>
        </div>

        {/* Checkbox Chips List */}
        <div className="flex flex-wrap items-center gap-2.5">
          {availableEventTypes.map((type) => {
            const isChecked = selectedEventTypes.includes(type);
            const count = eventTypeCounts[type] || 0;
            const config = EVENT_TYPE_CONFIG[type] || EVENT_TYPE_CONFIG['Other'];

            return (
              <label
                key={type}
                className={`flex items-center gap-2.5 px-3.5 py-2 rounded-xl border text-xs font-bold cursor-pointer transition-all select-none ${
                  isChecked
                    ? `${config.bg} ${config.activeBorder} ${config.color} shadow-xs scale-[1.01]`
                    : 'bg-slate-50 dark:bg-white/5 border-slate-200 dark:border-white/10 text-slate-400 hover:border-slate-300 dark:hover:border-white/20'
                }`}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => handleToggleEventType(type)}
                  className="w-4 h-4 rounded text-[#0A2451] focus:ring-[#FDBC12] accent-[#0A2451] cursor-pointer"
                />
                <span className="text-sm">{config.icon}</span>
                <span>{config.label}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${
                  isChecked
                    ? 'bg-white/90 dark:bg-black/40 shadow-xs'
                    : 'bg-slate-200 dark:bg-white/10 text-slate-400'
                }`}>
                  {count} {count === 1 ? 'meeting' : 'meetings'}
                </span>
              </label>
            );
          })}
        </div>

        {/* Dynamic Calculation Scope Indicator */}
        {selectedEventTypes.length === 0 ? (
          <div className="flex items-center gap-2 text-xs font-bold text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/40 p-3 rounded-xl border border-rose-200 dark:border-rose-900/40 animate-fadeIn">
            <FaExclamationTriangle className="flex-shrink-0 text-sm" />
            <span>No event types selected. Check at least one event type above to calculate chapter attendance.</span>
          </div>
        ) : (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-[11px] text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-white/[0.02] px-3.5 py-2 rounded-xl border border-slate-100 dark:border-white/5">
            <span className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse flex-shrink-0"></span>
              <span>
                Calculating attendance for: <strong className="text-slate-700 dark:text-slate-200">{selectedEventTypes.join(', ')}</strong> ({filteredEventsCount} total {filteredEventsCount === 1 ? 'meeting' : 'meetings'} included)
              </span>
            </span>
            <span className="font-semibold text-slate-400">
              ⚡ Instant Recalculation Active
            </span>
          </div>
        )}
      </div>

      {/* ================= EXECUTIVE SUMMARY KPI STATS ================= */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {/* Total Members */}
        <div className="bg-white dark:bg-[#131E32] rounded-xl p-4 shadow-sm border border-slate-200 dark:border-white/10 flex flex-col justify-between">
          <div className="flex items-center justify-between text-slate-500 dark:text-slate-400 text-xs font-bold">
            <span>Members</span>
            <FaUserCheck className="text-blue-500 text-base" />
          </div>
          <div className="mt-2">
            <span className="text-2xl font-black text-slate-800 dark:text-white">
              {chapterStats.total_members}
            </span>
          </div>
          <span className="text-[10px] text-slate-400 mt-1">Active roster</span>
        </div>

        {/* Average Attendance % */}
        <div className="bg-white dark:bg-[#131E32] rounded-xl p-4 shadow-sm border border-slate-200 dark:border-white/10 flex flex-col justify-between">
          <div className="flex items-center justify-between text-slate-500 dark:text-slate-400 text-xs font-bold">
            <span>Avg Attendance</span>
            <FaChartPie className="text-[#FDBC12] text-base" />
          </div>
          <div className="mt-2 flex items-baseline gap-1">
            <span className={`text-2xl font-black ${
              chapterStats.avg_attendance >= 75 ? 'text-emerald-500' :
              chapterStats.avg_attendance >= 50 ? 'text-amber-500' : 'text-rose-500'
            }`}>
              {chapterStats.avg_attendance}%
            </span>
          </div>
          <div className="w-full bg-slate-100 dark:bg-white/10 h-1.5 rounded-full overflow-hidden mt-1">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                chapterStats.avg_attendance >= 75 ? 'bg-emerald-500' :
                chapterStats.avg_attendance >= 50 ? 'bg-amber-500' : 'bg-rose-500'
              }`}
              style={{ width: `${Math.min(100, chapterStats.avg_attendance)}%` }}
            />
          </div>
        </div>

        {/* Total Present */}
        <div className="bg-white dark:bg-[#131E32] rounded-xl p-4 shadow-sm border border-slate-200 dark:border-white/10 flex flex-col justify-between">
          <div className="flex items-center justify-between text-slate-500 dark:text-slate-400 text-xs font-bold">
            <span>Total Present</span>
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
          </div>
          <div className="mt-2">
            <span className="text-2xl font-black text-emerald-600 dark:text-emerald-400">
              {chapterStats.total_present}
            </span>
          </div>
          <span className="text-[10px] text-emerald-600 dark:text-emerald-400 mt-1 font-semibold">10 pts per meeting</span>
        </div>

        {/* Total Absent */}
        <div className="bg-white dark:bg-[#131E32] rounded-xl p-4 shadow-sm border border-slate-200 dark:border-white/10 flex flex-col justify-between">
          <div className="flex items-center justify-between text-slate-500 dark:text-slate-400 text-xs font-bold">
            <span>Total Absent</span>
            <span className="w-2.5 h-2.5 rounded-full bg-rose-500" />
          </div>
          <div className="mt-2">
            <span className="text-2xl font-black text-rose-600 dark:text-rose-400">
              {chapterStats.total_absent}
            </span>
          </div>
          <span className="text-[10px] text-slate-400 mt-1">0 pts</span>
        </div>

        {/* Total Late & Substituted */}
        <div className="bg-white dark:bg-[#131E32] rounded-xl p-4 shadow-sm border border-slate-200 dark:border-white/10 flex flex-col justify-between">
          <div className="flex items-center justify-between text-slate-500 dark:text-slate-400 text-xs font-bold">
            <span>Late / Sub</span>
            <FaClock className="text-amber-500 text-xs" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="text-lg font-black text-amber-500">{chapterStats.total_late} <span className="text-[10px] font-normal text-slate-400">late</span></span>
            <span className="text-lg font-black text-indigo-500">{chapterStats.total_substituted} <span className="text-[10px] font-normal text-slate-400">sub</span></span>
          </div>
          <span className="text-[10px] text-slate-400 mt-1">Substitute/Late count</span>
        </div>

        {/* Flagged Members */}
        <div
          onClick={() => setStatusFilter(statusFilter === 'FLAGGED' ? 'ALL' : 'FLAGGED')}
          className={`rounded-xl p-4 shadow-sm border transition-all cursor-pointer flex flex-col justify-between ${
            chapterStats.flagged_members_count > 0
              ? 'bg-rose-50/80 dark:bg-rose-950/30 border-rose-300 dark:border-rose-500/30 hover:scale-[1.02]'
              : 'bg-white dark:bg-[#131E32] border-slate-200 dark:border-white/10'
          }`}
          title="Click to filter members flagged for 3+ consecutive absences"
        >
          <div className="flex items-center justify-between text-xs font-bold">
            <span className={chapterStats.flagged_members_count > 0 ? 'text-rose-700 dark:text-rose-300' : 'text-slate-500 dark:text-slate-400'}>
              Flagged (3+)
            </span>
            <FaExclamationTriangle className={chapterStats.flagged_members_count > 0 ? 'text-rose-600 animate-pulse' : 'text-slate-400'} />
          </div>
          <div className="mt-2">
            <span className={`text-2xl font-black ${
              chapterStats.flagged_members_count > 0 ? 'text-rose-600 dark:text-rose-400' : 'text-slate-700 dark:text-slate-300'
            }`}>
              {chapterStats.flagged_members_count}
            </span>
          </div>
          <span className="text-[10px] text-rose-600 dark:text-rose-400 mt-1 font-semibold">
            {statusFilter === 'FLAGGED' ? 'Showing filtered' : 'Click to filter'}
          </span>
        </div>
      </div>

      {/* ================= CONTROLS & FILTER BAR ================= */}
      <div className="bg-white dark:bg-[#131E32] p-4 rounded-2xl shadow-sm border border-slate-200 dark:border-white/10 flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
        {/* Search Input */}
        <div className="relative flex-1">
          <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm" />
          <input
            type="text"
            placeholder="Search member by name, company, designation, email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-xs sm:text-sm text-slate-800 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#FDBC12]"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-white"
            >
              <FaTimesCircle />
            </button>
          )}
        </div>

        {/* Filter Pills */}
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            onClick={() => setStatusFilter('ALL')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer ${
              statusFilter === 'ALL'
                ? 'bg-[#0A2451] text-[#FDBC12] shadow-sm'
                : 'bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-slate-300 hover:bg-slate-200'
            }`}
          >
            All ({members.length})
          </button>
          <button
            onClick={() => setStatusFilter('EXCELLENT')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer flex items-center gap-1 ${
              statusFilter === 'EXCELLENT'
                ? 'bg-emerald-600 text-white shadow-sm'
                : 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100'
            }`}
          >
            <span>🟢 ≥80%</span>
          </button>
          <button
            onClick={() => setStatusFilter('AVERAGE')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer flex items-center gap-1 ${
              statusFilter === 'AVERAGE'
                ? 'bg-amber-500 text-white shadow-sm'
                : 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 hover:bg-amber-100'
            }`}
          >
            <span>🟡 50-79%</span>
          </button>
          <button
            onClick={() => setStatusFilter('CRITICAL')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer flex items-center gap-1 ${
              statusFilter === 'CRITICAL'
                ? 'bg-rose-600 text-white shadow-sm'
                : 'bg-rose-50 dark:bg-rose-950/30 text-rose-700 dark:text-rose-300 hover:bg-rose-100'
            }`}
          >
            <span>🔴 &lt;50%</span>
          </button>
          <button
            onClick={() => setStatusFilter('FLAGGED')}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer flex items-center gap-1 ${
              statusFilter === 'FLAGGED'
                ? 'bg-rose-700 text-white shadow-sm'
                : 'bg-rose-100 dark:bg-rose-900/40 text-rose-800 dark:text-rose-200 hover:bg-rose-200'
            }`}
          >
            <FaExclamationTriangle className="text-xs" />
            <span>Flagged (3+)</span>
          </button>
        </div>

        {/* Sort & View Mode */}
        <div className="flex items-center gap-2">
          <select
            value={sortBy}
            onChange={(e: any) => setSortBy(e.target.value)}
            className="px-3 py-2 rounded-xl bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 text-xs font-bold text-slate-700 dark:text-slate-200 outline-none cursor-pointer"
          >
            <option value="PERCENT_DESC" className="bg-white dark:bg-[#0A2451]">Attendance % (High → Low)</option>
            <option value="PERCENT_ASC" className="bg-white dark:bg-[#0A2451]">Attendance % (Low → High)</option>
            <option value="PRESENT_DESC" className="bg-white dark:bg-[#0A2451]">Most Presences</option>
            <option value="ABSENT_DESC" className="bg-white dark:bg-[#0A2451]">Most Absences</option>
            <option value="STREAK_DESC" className="bg-white dark:bg-[#0A2451]">Longest Absence Streak</option>
            <option value="NAME_ASC" className="bg-white dark:bg-[#0A2451]">Member Name (A → Z)</option>
          </select>

          {/* Table vs Grid Toggle */}
          <div className="flex items-center bg-slate-100 dark:bg-white/5 p-1 rounded-xl border border-slate-200 dark:border-white/10">
            <button
              onClick={() => setViewMode('TABLE')}
              className={`p-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                viewMode === 'TABLE'
                  ? 'bg-white dark:bg-[#0A2451] text-[#FDBC12] shadow-xs'
                  : 'text-slate-400 hover:text-slate-700 dark:hover:text-white'
              }`}
              title="Table View"
            >
              <FaList />
            </button>
            <button
              onClick={() => setViewMode('GRID')}
              className={`p-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                viewMode === 'GRID'
                  ? 'bg-white dark:bg-[#0A2451] text-[#FDBC12] shadow-xs'
                  : 'text-slate-400 hover:text-slate-700 dark:hover:text-white'
              }`}
              title="Grid Card View"
            >
              <FaThLarge />
            </button>
          </div>
        </div>
      </div>

      {/* ================= MEMBERS LIST / TABLE ================= */}
      {loading ? (
        <div className="bg-white dark:bg-[#131E32] rounded-2xl p-12 text-center border border-slate-200 dark:border-white/10">
          <FaSyncAlt className="animate-spin text-3xl text-[#FDBC12] mx-auto mb-3" />
          <p className="text-sm font-bold text-slate-600 dark:text-slate-300">Loading member attendance records & event sheets...</p>
        </div>
      ) : filteredMembers.length === 0 ? (
        <div className="bg-white dark:bg-[#131E32] rounded-2xl p-12 text-center border border-slate-200 dark:border-white/10">
          <FaUserTimes className="text-4xl text-slate-300 dark:text-slate-600 mx-auto mb-3" />
          <h3 className="text-base font-bold text-slate-700 dark:text-slate-200">No members found</h3>
          <p className="text-xs text-slate-400 mt-1">
            {selectedEventTypes.length === 0
              ? 'Select at least one event type checkbox above to view calculation.'
              : 'Try changing your search terms or filters.'}
          </p>
        </div>
      ) : viewMode === 'TABLE' ? (
        /* TABLE VIEW */
        <div className="bg-white dark:bg-[#131E32] rounded-2xl shadow-sm border border-slate-200 dark:border-white/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 dark:bg-white/5 border-b border-slate-200 dark:border-white/10 text-[11px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">
                  <th className="py-3 px-4">Member</th>
                  <th className="py-3 px-3 text-center">Meetings</th>
                  <th className="py-3 px-3 text-center">Present</th>
                  <th className="py-3 px-3 text-center">Absent</th>
                  <th className="py-3 px-3 text-center">Late</th>
                  <th className="py-3 px-3 text-center">Sub</th>
                  <th className="py-3 px-3 text-center">Points</th>
                  <th className="py-3 px-4 text-center">Attendance %</th>
                  <th className="py-3 px-3 text-center">Status / Streak</th>
                  <th className="py-3 px-4 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-white/5 text-xs sm:text-sm">
                {filteredMembers.map((m) => {
                  const pct = m.attendance_percentage;
                  const isFlagged = m.flagged_3_absences || m.consecutive_absences >= 3;

                  return (
                    <tr
                      key={m.user_id}
                      className={`hover:bg-slate-50/80 dark:hover:bg-white/[0.03] transition-colors ${
                        isFlagged ? 'bg-rose-50/30 dark:bg-rose-950/10' : ''
                      }`}
                    >
                      {/* Member Info */}
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <img
                            src={
                              m.profile
                                ? (m.profile.startsWith('http')
                                    ? m.profile
                                    : `https://rmbbackend.rmbjaipur.com/${m.profile.replace(/\\/g, '/')}`)
                                : 'https://via.placeholder.com/150'
                            }
                            alt={m.name}
                            className="w-10 h-10 rounded-full object-cover border border-slate-200 dark:border-white/10 flex-shrink-0"
                            onError={(e: any) => { e.target.src = 'https://via.placeholder.com/150'; }}
                          />
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="font-bold text-slate-800 dark:text-white truncate">
                                {m.name}
                              </span>
                              {isFlagged && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-black bg-rose-100 dark:bg-rose-900/60 text-rose-700 dark:text-rose-300 flex items-center gap-0.5" title="Flagged: 3+ Consecutive Absences in selected event types">
                                  <FaExclamationTriangle />
                                  <span>{m.consecutive_absences} Absences</span>
                                </span>
                              )}
                            </div>
                            <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                              {m.company_name || m.designation || 'Member'}
                            </p>
                            <p className="text-[10px] text-slate-400 truncate">
                              {m.number || m.email}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Total Meetings */}
                      <td className="py-3 px-3 text-center font-bold text-slate-600 dark:text-slate-300">
                        {m.total_meetings}
                      </td>

                      {/* Total Present */}
                      <td className="py-3 px-3 text-center">
                        <span className="inline-block px-2.5 py-1 rounded-full font-black text-xs bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300">
                          {m.total_present}
                        </span>
                      </td>

                      {/* Total Absent */}
                      <td className="py-3 px-3 text-center">
                        <span className={`inline-block px-2.5 py-1 rounded-full font-black text-xs ${
                          m.total_absent > 0
                            ? 'bg-rose-100 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300'
                            : 'bg-slate-100 dark:bg-white/5 text-slate-400'
                        }`}>
                          {m.total_absent}
                        </span>
                      </td>

                      {/* Total Late */}
                      <td className="py-3 px-3 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full font-bold text-xs ${
                          m.total_late > 0
                            ? 'bg-amber-100 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300'
                            : 'text-slate-400'
                        }`}>
                          {m.total_late}
                        </span>
                      </td>

                      {/* Total Substituted */}
                      <td className="py-3 px-3 text-center">
                        <span className={`inline-block px-2 py-0.5 rounded-full font-bold text-xs ${
                          m.total_substituted > 0
                            ? 'bg-indigo-100 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300'
                            : 'text-slate-400'
                        }`}>
                          {m.total_substituted}
                        </span>
                      </td>

                      {/* Points */}
                      <td className="py-3 px-3 text-center font-black text-amber-600 dark:text-amber-400">
                        +{m.attendance_points}
                      </td>

                      {/* Attendance % */}
                      <td className="py-3 px-4">
                        <div className="w-28 mx-auto space-y-1">
                          <div className="flex items-center justify-between text-xs font-black">
                            <span className={
                              pct >= 75 ? 'text-emerald-600 dark:text-emerald-400' :
                              pct >= 50 ? 'text-amber-600 dark:text-amber-400' : 'text-rose-600 dark:text-rose-400'
                            }>
                              {pct}%
                            </span>
                            <span className="text-[10px] text-slate-400 font-semibold">
                              {m.total_attended}/{m.effective_meetings}
                            </span>
                          </div>
                          <div className="w-full bg-slate-100 dark:bg-white/10 h-2 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all duration-300 ${
                                pct >= 75 ? 'bg-emerald-500' :
                                pct >= 50 ? 'bg-amber-500' : 'bg-rose-500'
                              }`}
                              style={{ width: `${Math.min(100, pct)}%` }}
                            />
                          </div>
                        </div>
                      </td>

                      {/* Streak / Status */}
                      <td className="py-3 px-3 text-center">
                        {isFlagged ? (
                          <span className="px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-rose-600 text-white flex items-center justify-center gap-1 mx-auto w-max shadow-xs">
                            <FaExclamationTriangle />
                            <span>Streak {m.consecutive_absences}</span>
                          </span>
                        ) : m.consecutive_absences > 0 ? (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 dark:bg-amber-950/50 text-amber-800 dark:text-amber-300">
                            {m.consecutive_absences} Absent streak
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 dark:bg-emerald-950/50 text-emerald-800 dark:text-emerald-300 flex items-center justify-center gap-1 mx-auto w-max">
                            <FaCheck className="text-[9px]" />
                            <span>Active</span>
                          </span>
                        )}
                      </td>

                      {/* Action */}
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() => handleOpenMemberSheet(m)}
                          className="px-3 py-1.5 rounded-xl font-bold text-xs bg-[#0A2451] hover:bg-[#133266] text-[#FDBC12] transition cursor-pointer shadow-xs active:scale-95 flex items-center gap-1.5 ml-auto"
                        >
                          <span>View Sheet</span>
                          <FaExternalLinkAlt className="text-[10px]" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* GRID CARD VIEW */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredMembers.map((m) => {
            const pct = m.attendance_percentage;
            const isFlagged = m.flagged_3_absences || m.consecutive_absences >= 3;

            return (
              <div
                key={m.user_id}
                className={`bg-white dark:bg-[#131E32] rounded-2xl p-5 shadow-sm border transition-all hover:shadow-md ${
                  isFlagged
                    ? 'border-rose-300 dark:border-rose-500/40 bg-rose-50/20'
                    : 'border-slate-200 dark:border-white/10'
                }`}
              >
                {/* Top Member Info */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <img
                      src={
                        m.profile
                          ? (m.profile.startsWith('http')
                              ? m.profile
                              : `https://rmbbackend.rmbjaipur.com/${m.profile.replace(/\\/g, '/')}`)
                          : 'https://via.placeholder.com/150'
                      }
                      alt={m.name}
                      className="w-12 h-12 rounded-full object-cover border-2 border-slate-200 dark:border-white/10 flex-shrink-0"
                      onError={(e: any) => { e.target.src = 'https://via.placeholder.com/150'; }}
                    />
                    <div>
                      <h4 className="font-bold text-slate-800 dark:text-white text-sm line-clamp-1">
                        {m.name}
                      </h4>
                      <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1">
                        {m.company_name || m.designation || 'Member'}
                      </p>
                      <p className="text-[11px] text-slate-400">{m.number}</p>
                    </div>
                  </div>

                  <div className="text-right">
                    <span className={`text-xl font-black ${
                      pct >= 75 ? 'text-emerald-500' : pct >= 50 ? 'text-amber-500' : 'text-rose-500'
                    }`}>
                      {pct}%
                    </span>
                    <p className="text-[10px] text-slate-400 font-semibold">{m.total_attended}/{m.effective_meetings}</p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="w-full bg-slate-100 dark:bg-white/10 h-2 rounded-full overflow-hidden mt-4">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${
                      pct >= 75 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-rose-500'
                    }`}
                    style={{ width: `${Math.min(100, pct)}%` }}
                  />
                </div>

                {/* 4-Stat Breakdown Grid */}
                <div className="grid grid-cols-4 gap-2 mt-4 text-center">
                  <div className="bg-emerald-50 dark:bg-emerald-950/40 p-2 rounded-xl border border-emerald-100 dark:border-emerald-900/30">
                    <span className="text-xs font-black text-emerald-700 dark:text-emerald-300 block">
                      {m.total_present}
                    </span>
                    <span className="text-[9px] uppercase font-bold text-emerald-600 dark:text-emerald-400">Present</span>
                  </div>
                  <div className="bg-rose-50 dark:bg-rose-950/40 p-2 rounded-xl border border-rose-100 dark:border-rose-900/30">
                    <span className="text-xs font-black text-rose-700 dark:text-rose-300 block">
                      {m.total_absent}
                    </span>
                    <span className="text-[9px] uppercase font-bold text-rose-600 dark:text-rose-400">Absent</span>
                  </div>
                  <div className="bg-amber-50 dark:bg-amber-950/40 p-2 rounded-xl border border-amber-100 dark:border-amber-900/30">
                    <span className="text-xs font-black text-amber-700 dark:text-amber-300 block">
                      {m.total_late}
                    </span>
                    <span className="text-[9px] uppercase font-bold text-amber-600 dark:text-amber-400">Late</span>
                  </div>
                  <div className="bg-indigo-50 dark:bg-indigo-950/40 p-2 rounded-xl border border-indigo-100 dark:border-indigo-900/30">
                    <span className="text-xs font-black text-indigo-700 dark:text-indigo-300 block">
                      {m.total_substituted}
                    </span>
                    <span className="text-[9px] uppercase font-bold text-indigo-600 dark:text-indigo-400">Sub</span>
                  </div>
                </div>

                {/* Flagged Streak Notification */}
                {isFlagged && (
                  <div className="mt-3 px-3 py-1.5 rounded-xl bg-rose-100 dark:bg-rose-950/60 border border-rose-200 dark:border-rose-900 text-rose-800 dark:text-rose-300 text-xs font-bold flex items-center gap-2">
                    <FaExclamationTriangle className="text-rose-600 flex-shrink-0" />
                    <span>Flagged: {m.consecutive_absences} consecutive absences!</span>
                  </div>
                )}

                {/* Bottom Action */}
                <div className="mt-4 pt-3 border-t border-slate-100 dark:border-white/5 flex items-center justify-between">
                  <span className="text-xs font-black text-amber-600 dark:text-amber-400">
                    +{m.attendance_points} Points
                  </span>
                  <button
                    onClick={() => handleOpenMemberSheet(m)}
                    className="px-3.5 py-1.5 rounded-xl font-bold text-xs bg-[#0A2451] hover:bg-[#133266] text-[#FDBC12] transition cursor-pointer shadow-xs active:scale-95 flex items-center gap-1.5"
                  >
                    <span>Attendance Sheet</span>
                    <FaExternalLinkAlt className="text-[10px]" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ================= INDIVIDUAL MEMBER ATTENDANCE DRILLDOWN MODAL ================= */}
      {selectedMember && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-black/60 backdrop-blur-sm animate-fadeIn overflow-y-auto">
          <div className="relative w-full max-w-4xl bg-white dark:bg-[#0F1C36] rounded-3xl shadow-2xl border border-slate-200 dark:border-white/10 overflow-hidden flex flex-col max-h-[92vh]">
            
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-[#0A2451] via-[#103366] to-[#0A2451] p-5 sm:p-6 text-white border-b border-white/10 flex-shrink-0">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-4">
                  <img
                    src={
                      selectedMember.profile
                        ? (selectedMember.profile.startsWith('http')
                            ? selectedMember.profile
                            : `https://rmbbackend.rmbjaipur.com/${selectedMember.profile.replace(/\\/g, '/')}`)
                        : 'https://via.placeholder.com/150'
                    }
                    alt={selectedMember.name}
                    className="w-16 h-16 rounded-2xl object-cover border-2 border-[#FDBC12] shadow-lg flex-shrink-0"
                    onError={(e: any) => { e.target.src = 'https://via.placeholder.com/150'; }}
                  />
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="text-lg sm:text-2xl font-black text-white">
                        {selectedMember.name}
                      </h2>
                      {selectedMember.flagged_3_absences && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-rose-600 text-white flex items-center gap-1">
                          <FaExclamationTriangle />
                          <span>Flagged (3+ Absences)</span>
                        </span>
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-amber-300 font-semibold flex items-center gap-2 mt-0.5">
                      <FaBuilding className="text-xs" />
                      <span>{selectedMember.company_name || 'Individual Member'}</span>
                      {selectedMember.designation && <span>• {selectedMember.designation}</span>}
                    </p>
                    <div className="flex flex-wrap items-center gap-3 text-xs text-slate-300 mt-1">
                      {selectedMember.email && (
                        <span className="flex items-center gap-1">
                          <FaEnvelope className="text-[10px] text-slate-400" />
                          <span>{selectedMember.email}</span>
                        </span>
                      )}
                      {selectedMember.number && (
                        <span className="flex items-center gap-1">
                          <FaPhone className="text-[10px] text-slate-400" />
                          <span>{selectedMember.number}</span>
                        </span>
                      )}
                      <span className="flex items-center gap-1 text-[#FDBC12] font-bold">
                        <FaAward className="text-xs" />
                        <span>+{selectedMember.attendance_points} Attendance Points</span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* Close & Action Buttons */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={exportSingleMemberToCSV}
                    className="px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold text-xs flex items-center gap-1.5 transition cursor-pointer"
                    title="Export Member Sheet as CSV"
                  >
                    <FaDownload />
                    <span className="hidden sm:inline">Export</span>
                  </button>
                  <button
                    onClick={() => window.print()}
                    className="px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/20 text-white font-bold text-xs flex items-center gap-1.5 transition cursor-pointer"
                    title="Print Member Attendance Report"
                  >
                    <FaPrint />
                    <span className="hidden sm:inline">Print</span>
                  </button>
                  <button
                    onClick={() => setSelectedMember(null)}
                    className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-base transition cursor-pointer"
                    title="Close"
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>

              {/* Attendance KPI Cards inside Member Modal */}
              <div className="grid grid-cols-2 sm:grid-cols-6 gap-2 sm:gap-3 mt-5">
                <div className="bg-white/10 backdrop-blur-md rounded-xl p-2.5 text-center border border-white/10">
                  <span className="text-[10px] uppercase font-bold text-slate-300 block">Meetings</span>
                  <span className="text-lg font-black text-white">{selectedMember.total_meetings}</span>
                </div>
                <div className="bg-emerald-500/20 backdrop-blur-md rounded-xl p-2.5 text-center border border-emerald-400/30">
                  <span className="text-[10px] uppercase font-bold text-emerald-300 block">Present</span>
                  <span className="text-lg font-black text-emerald-400">{selectedMember.total_present}</span>
                </div>
                <div className="bg-rose-500/20 backdrop-blur-md rounded-xl p-2.5 text-center border border-rose-400/30">
                  <span className="text-[10px] uppercase font-bold text-rose-300 block">Absent</span>
                  <span className="text-lg font-black text-rose-400">{selectedMember.total_absent}</span>
                </div>
                <div className="bg-amber-500/20 backdrop-blur-md rounded-xl p-2.5 text-center border border-amber-400/30">
                  <span className="text-[10px] uppercase font-bold text-amber-300 block">Late</span>
                  <span className="text-lg font-black text-amber-400">{selectedMember.total_late}</span>
                </div>
                <div className="bg-indigo-500/20 backdrop-blur-md rounded-xl p-2.5 text-center border border-indigo-400/30">
                  <span className="text-[10px] uppercase font-bold text-indigo-300 block">Substitute</span>
                  <span className="text-lg font-black text-indigo-400">{selectedMember.total_substituted}</span>
                </div>
                <div className="bg-white/10 backdrop-blur-md rounded-xl p-2.5 text-center border border-white/10">
                  <span className="text-[10px] uppercase font-bold text-[#FDBC12] block">Attendance %</span>
                  <span className="text-lg font-black text-[#FDBC12]">{selectedMember.attendance_percentage}%</span>
                </div>
              </div>
            </div>

            {/* Modal Body / History List */}
            <div className="p-4 sm:p-6 overflow-y-auto flex-1 space-y-4">
              {/* Consecutive Absence Alert Banner */}
              {selectedMember.flagged_3_absences && (
                <div className="p-4 rounded-2xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/50 flex items-start gap-3 text-rose-800 dark:text-rose-200">
                  <FaExclamationTriangle className="text-xl text-rose-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-black text-sm">Attendance Rule Alert: 3+ Consecutive Absences</h4>
                    <p className="text-xs text-rose-700 dark:text-rose-300 mt-0.5">
                      This member has missed <strong>{selectedMember.consecutive_absences} consecutive chapter meetings</strong> (in selected event types: {selectedEventTypes.join(', ')}).
                      Under chapter bylaws, members with 3+ consecutive unexcused absences require committee review or personal coordinator follow-up.
                    </p>
                  </div>
                </div>
              )}

              {/* Header and Toggle */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <h3 className="text-sm sm:text-base font-black text-slate-800 dark:text-white flex items-center gap-2">
                    <FaCalendarAlt className="text-[#FDBC12]" />
                    Event &amp; Meeting Attendance History
                  </h3>
                  <p className="text-xs text-slate-400">
                    Chronological record of meetings with event types, recorded status, substitute names, and notes.
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setFilterModalBySelectedTypes(prev => !prev)}
                    className={`px-3 py-1 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer ${
                      filterModalBySelectedTypes
                        ? 'bg-[#0A2451] text-[#FDBC12] border border-[#FDBC12]/30'
                        : 'bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-slate-300'
                    }`}
                    title="Toggle filtering history by selected event types"
                  >
                    <span>{filterModalBySelectedTypes ? 'Filtered by Selected Types' : 'Showing All Types'}</span>
                    <span className="px-1.5 py-0.2 rounded-full text-[10px] bg-white/20">
                      {modalDisplayHistory.length}
                    </span>
                  </button>
                </div>
              </div>

              {/* History Table */}
              {historyLoading ? (
                <div className="py-12 text-center">
                  <FaSyncAlt className="animate-spin text-2xl text-[#FDBC12] mx-auto mb-2" />
                  <p className="text-xs font-bold text-slate-500">Loading meeting history...</p>
                </div>
              ) : modalDisplayHistory.length === 0 ? (
                <div className="py-12 text-center bg-slate-50 dark:bg-white/5 rounded-2xl border border-slate-200 dark:border-white/10">
                  <FaCalendarAlt className="text-3xl text-slate-300 dark:text-slate-600 mx-auto mb-2" />
                  <p className="text-xs font-bold text-slate-500">
                    {selectedEventTypes.length === 0
                      ? 'No event types selected. Check event types to view attendance.'
                      : 'No meeting events found matching the selected event types.'}
                  </p>
                </div>
              ) : (
                <div className="border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden shadow-xs">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="bg-slate-50 dark:bg-white/5 border-b border-slate-200 dark:border-white/10 text-[10px] font-black uppercase tracking-wider text-slate-500 dark:text-slate-400">
                        <th className="py-2.5 px-3">Date</th>
                        <th className="py-2.5 px-3">Meeting / Event</th>
                        <th className="py-2.5 px-3 text-center">Type</th>
                        <th className="py-2.5 px-3 text-center">Status</th>
                        <th className="py-2.5 px-3 text-center">Points</th>
                        <th className="py-2.5 px-3">Substitute / Notes</th>
                        <th className="py-2.5 px-3 text-right">Quick Edit</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 dark:divide-white/5">
                      {modalDisplayHistory.map((rec) => {
                        const isPres = rec.status === 'Present';
                        const isAbs = rec.status === 'Absent';
                        const isLate = rec.status === 'Late';
                        const isSub = rec.status === 'Substituted';
                        const isNa = rec.status === 'NA';
                        const evType = rec.event_type || 'General Meeting';
                        const typeConfig = EVENT_TYPE_CONFIG[evType] || EVENT_TYPE_CONFIG['Other'];

                        return (
                          <tr key={rec.event_id} className="hover:bg-slate-50/60 dark:hover:bg-white/[0.02] transition-colors">
                            {/* Date */}
                            <td className="py-3 px-3 font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap">
                              {rec.event_date ? new Date(rec.event_date).toLocaleDateString('en-IN', {
                                day: '2-digit', month: 'short', year: 'numeric'
                              }) : '—'}
                            </td>

                            {/* Meeting Title */}
                            <td className="py-3 px-3">
                              <span className="font-bold text-slate-800 dark:text-white block line-clamp-1">
                                {rec.event_title}
                              </span>
                              {rec.event_location && (
                                <span className="text-[10px] text-slate-400 line-clamp-1">
                                  📍 {rec.event_location}
                                </span>
                              )}
                            </td>

                            {/* Event Type Badge */}
                            <td className="py-3 px-3 text-center whitespace-nowrap">
                              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold ${typeConfig.badgeBg}`}>
                                <span>{typeConfig.icon}</span>
                                <span>{typeConfig.label}</span>
                              </span>
                            </td>

                            {/* Status */}
                            <td className="py-3 px-3 text-center whitespace-nowrap">
                              <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black ${
                                isPres ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-800 dark:text-emerald-300' :
                                isAbs ? 'bg-rose-100 dark:bg-rose-950/60 text-rose-800 dark:text-rose-300' :
                                isLate ? 'bg-amber-100 dark:bg-amber-950/60 text-amber-800 dark:text-amber-300' :
                                isSub ? 'bg-indigo-100 dark:bg-indigo-950/60 text-indigo-800 dark:text-indigo-300' :
                                'bg-slate-100 dark:bg-white/10 text-slate-600 dark:text-slate-400'
                              }`}>
                                {isPres && <FaCheck className="text-[9px]" />}
                                {isAbs && <FaTimes className="text-[9px]" />}
                                {isLate && <FaClock className="text-[9px]" />}
                                {isSub && <FaUserFriends className="text-[9px]" />}
                                <span>{rec.status}</span>
                              </span>
                            </td>

                            {/* Points */}
                            <td className="py-3 px-3 text-center font-bold">
                              <span className={rec.points > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-slate-400'}>
                                {rec.points > 0 ? `+${rec.points}` : '0'}
                              </span>
                            </td>

                            {/* Substitute / Notes */}
                            <td className="py-3 px-3">
                              {isSub && (rec.substitute_name || rec.substitute_phone) ? (
                                <div className="text-[11px]">
                                  <span className="font-bold text-indigo-600 dark:text-indigo-400">
                                    Sub: {rec.substitute_name}
                                  </span>
                                  {rec.substitute_phone && <span className="text-slate-400 ml-1">({rec.substitute_phone})</span>}
                                  {rec.substitute_company && <span className="text-slate-400 block">{rec.substitute_company}</span>}
                                </div>
                              ) : rec.notes ? (
                                <span className="text-[11px] text-slate-500 dark:text-slate-400 italic line-clamp-1">
                                  {rec.notes}
                                </span>
                              ) : (
                                <span className="text-slate-300 dark:text-slate-600">—</span>
                              )}
                            </td>

                            {/* Edit Action */}
                            <td className="py-3 px-3 text-right">
                              <button
                                onClick={() => handleStartEdit(rec)}
                                className="px-2.5 py-1 rounded-lg font-bold text-[11px] bg-slate-100 hover:bg-[#FDBC12] text-slate-700 hover:text-[#0A2451] dark:bg-white/10 dark:text-slate-300 dark:hover:bg-[#FDBC12] dark:hover:text-[#0A2451] transition cursor-pointer"
                                title="Edit status for this meeting"
                              >
                                <FaEdit className="inline mr-1" />
                                Edit
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-slate-50 dark:bg-[#0A162D] px-6 py-4 border-t border-slate-200 dark:border-white/10 flex items-center justify-between flex-shrink-0">
              <span className="text-xs text-slate-500 dark:text-slate-400 font-semibold">
                Member ID #{selectedMember.user_id} • Society Year {societyYear} • Event Types: {selectedEventTypes.join(', ') || 'None'}
              </span>
              <button
                onClick={() => setSelectedMember(null)}
                className="px-5 py-2 rounded-xl bg-slate-200 hover:bg-slate-300 dark:bg-white/10 dark:hover:bg-white/20 text-slate-800 dark:text-white font-bold text-xs transition cursor-pointer"
              >
                Close Sheet
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= INLINE EDIT RECORD MODAL ================= */}
      {editingRecord && selectedMember && (
        <div className="fixed inset-0 z-60 flex items-center justify-center p-4 bg-black/70 backdrop-blur-xs animate-fadeIn">
          <div className="relative w-full max-w-md bg-white dark:bg-[#131E32] rounded-3xl shadow-2xl border border-slate-200 dark:border-white/10 p-6 space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-white/10 pb-3">
              <div>
                <h3 className="text-base font-black text-slate-800 dark:text-white">
                  Update Meeting Attendance
                </h3>
                <p className="text-xs text-slate-400 mt-0.5">
                  {selectedMember.name} • {editingRecord.event_title} ({editingRecord.event_type || 'General Meeting'})
                </p>
              </div>
              <button
                onClick={() => setEditingRecord(null)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-white"
              >
                <FaTimes />
              </button>
            </div>

            {/* Status Selector */}
            <div className="space-y-1.5">
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                Attendance Status
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'Present', label: 'Present (+10 pts)', color: 'border-emerald-500 text-emerald-600' },
                  { key: 'Absent', label: 'Absent (0 pts)', color: 'border-rose-500 text-rose-600' },
                  { key: 'Late', label: 'Late (+5 pts)', color: 'border-amber-500 text-amber-600' },
                  { key: 'Substituted', label: 'Substituted (+10 pts)', color: 'border-indigo-500 text-indigo-600' },
                  { key: 'NA', label: 'Excused / NA (0 pts)', color: 'border-slate-500 text-slate-500' }
                ].map(opt => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setEditStatus(opt.key as any)}
                    className={`py-2 px-3 rounded-xl border text-xs font-bold text-left transition cursor-pointer ${
                      editStatus === opt.key
                        ? 'bg-[#0A2451] text-[#FDBC12] border-[#FDBC12] shadow-sm'
                        : 'border-slate-200 dark:border-white/10 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/5'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Substitution Fields if Substituted */}
            {editStatus === 'Substituted' && (
              <div className="p-3 rounded-2xl bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-900/40 space-y-2.5">
                <span className="text-xs font-black text-indigo-800 dark:text-indigo-300 block">
                  Substitute Details
                </span>
                <div>
                  <label className="text-[11px] font-bold text-slate-600 dark:text-slate-300">Substitute Full Name *</label>
                  <input
                    type="text"
                    value={editSubName}
                    onChange={(e) => setEditSubName(e.target.value)}
                    placeholder="Enter substitute's name"
                    className="w-full mt-1 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0A2451] text-xs text-slate-800 dark:text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[11px] font-bold text-slate-600 dark:text-slate-300">Phone</label>
                    <input
                      type="text"
                      value={editSubPhone}
                      onChange={(e) => setEditSubPhone(e.target.value)}
                      placeholder="Phone number"
                      className="w-full mt-1 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0A2451] text-xs text-slate-800 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold text-slate-600 dark:text-slate-300">Company</label>
                    <input
                      type="text"
                      value={editSubCompany}
                      onChange={(e) => setEditSubCompany(e.target.value)}
                      placeholder="Company"
                      className="w-full mt-1 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0A2451] text-xs text-slate-800 dark:text-white"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-1">
              <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                Notes / Reason
              </label>
              <textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Optional notes or remarks..."
                rows={2}
                className="w-full px-3 py-2 rounded-xl border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 text-xs text-slate-800 dark:text-white"
              />
            </div>

            {/* Notification message */}
            {recordSaveMsg && (
              <p className="text-xs font-bold text-center text-emerald-600 dark:text-emerald-400">
                {recordSaveMsg}
              </p>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100 dark:border-white/10">
              <button
                type="button"
                onClick={() => setEditingRecord(null)}
                className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 transition"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveSingleRecord}
                disabled={savingRecord}
                className="px-5 py-2 rounded-xl text-xs font-black bg-[#FDBC12] hover:bg-amber-400 text-[#0A2451] shadow-md transition flex items-center gap-1.5 cursor-pointer"
              >
                {savingRecord ? <FaSyncAlt className="animate-spin" /> : <FaSave />}
                <span>{savingRecord ? 'Saving...' : 'Save Attendance'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberAttendance;
